// Print a PDF through the browser's native print dialog without leaving the page:
// fetch it as a blob so the hidden iframe is same-origin (a blob: URL), which lets
// print() be called on it. Same mechanism LabelView's Imprimir button uses for every
// packing label (pdf-to-base64 → blob → iframe → print). If the fetch fails — the S3
// redirect behind a rails_blob_path may lack CORS — fall back to opening the PDF in
// a new tab (a top-level navigation is not subject to CORS) and auto-print there.
//
// Resolves 'iframe' | 'timeout' | 'fallback' | 'blocked' | 'skipped'; never rejects.
// Printing itself is fire-and-forget: the label already exists server-side, so a print
// problem is a notice for the operator (onFallback + the resolved value), never a failed
// pipeline — but the resolved value must be HONEST, because the caller records whether
// the label reached the printer.
//
//   'iframe'   printed in place: print() returned
//   'timeout'  the blob never loaded and the safety timer expired — nothing was printed
//   'fallback' the PDF was opened in a new tab instead; the operator prints from there
//   'blocked'  even the new tab was blocked by the popup blocker — the operator needs the
//              "open the label" link
//   'skipped'  there was no url to print
const CLEANUP_MS = 60000
// The print dialog is modal in every desktop browser, so `load` normally fires (and print()
// returns) before this; the timer only exists so a blob that never loads cannot hang the
// caller's pipeline forever. It resolves 'timeout', not 'iframe': nothing was printed.
const PRINT_SETTLE_TIMEOUT_MS = 15000
const FETCH_TIMEOUT_MS = 30000
const NEW_TAB_PRINT_MS = 1500

// A caller's onFallback is a setState in practice; a throw from it must not turn a print
// notice into a rejected promise.
const notify = (onFallback, error) => {
  if (!onFallback) return
  try {
    onFallback(error)
  } catch (_) {
    /* the operator notice is best effort */
  }
}

// At most one helper iframe is alive at a time. A receiving station prints all day; the
// 60 s cleanup ceiling stays (the print job has to spool before the blob is revoked), but
// a NEW print disposes the previous helper immediately instead of letting up to twenty
// iframes and their blobs pile up in a shift.
let activeHelper = null

const disposeHelper = helper => {
  if (!helper) return
  if (helper.cleanupTimer) clearTimeout(helper.cleanupTimer)
  try {
    URL.revokeObjectURL(helper.blobUrl)
  } catch (_) {
    /* already revoked, or a document without blob: support */
  }
  if (helper.iframe && helper.iframe.parentNode) helper.iframe.parentNode.removeChild(helper.iframe)
  if (activeHelper === helper) activeHelper = null
}

// Returns whether the tab actually opened AND could be handed the URL: a popup blocker
// gives back null, and that is a different notice for the operator ('blocked') than a
// successful fallback tab.
//
// No `noopener,noreferrer` windowFeatures on purpose: per the HTML spec, `window.open`
// returns null whenever `noopener` (or `noreferrer`, which implies it) is in the features
// string, so the tab opens but the handle is gone — the helper would report 'blocked' on
// the successful path and could never auto-print the fallback tab. Nulling `opener` by
// hand keeps exactly the same protection and gives back a usable handle — but only if the
// tab is opened on about:blank FIRST: a tab opened straight onto the PDF is cross-origin
// by the time the write lands, so the assignment is dropped and the new tab keeps a live
// `window.opener` pointing at the WMS.
const openInNewTab = url => {
  let tab = null
  try {
    tab = window.open('', '_blank')
  } catch (_) {
    /* some hardened environments throw instead of returning null */
  }
  if (!tab) return false
  try {
    tab.opener = null
  } catch (_) {
    // Severing the opener is the point of the sequence: a tab that refuses it is closed
    // rather than navigated to the label.
    try {
      tab.close()
    } catch (_) {
      /* nothing else to do about it */
    }
    return false
  }
  try {
    tab.location.href = url
  } catch (_) {
    try {
      tab.close()
    } catch (_) {
      /* nothing else to do about it */
    }
    return false
  }
  // `load` and the timer are two triggers for one print: without this flag a tab that
  // loads within 1.5 s gets two print dialogs (and two copies on the printer).
  let printed = false
  const tryPrint = () => {
    if (printed) return
    printed = true
    try {
      tab.focus()
      tab.print()
    } catch (_) {
      /* the tab may be cross-origin by now; the operator prints from the viewer */
    }
  }
  try {
    tab.addEventListener('load', tryPrint)
  } catch (_) {
    /* a cross-origin tab can refuse listeners */
  }
  setTimeout(tryPrint, NEW_TAB_PRINT_MS)
  return true
}

// A 200 is not proof that the body is the label. An expired session redirects to the login
// HTML, and a WAF answers with its own interstitial — both are 200s that would be printed AS
// the label. `application/octet-stream` is a real answer from S3 for a PDF, but a blob typed
// that way downloads instead of rendering, so the iframe's `load` never fires and the print
// times out; it is re-typed below.
const PRINTABLE_TYPES = ['application/pdf', 'application/octet-stream']

// Bounded: a fetch that never answers (proxy black hole) aborts and takes the fallback path.
const fetchPdfBlob = async url => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    const response = await fetch(url, { signal: controller.signal })
    if (!response.ok) throw new Error(`fetch failed: ${response.status}`)
    const contentType = String(response.headers?.get?.('content-type') || '').toLowerCase()
    if (!PRINTABLE_TYPES.some(type => contentType.startsWith(type))) throw new Error(`unexpected content-type: ${contentType || 'none'}`)
    const blob = await response.blob()
    return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
  } finally {
    clearTimeout(timer)
  }
}

// Resolves only once print() has actually run (or the safety timer fired), so a caller
// printing several labels prints them one modal dialog at a time.
const printBlobInIframe = (blob, url, onFallback) =>
  new Promise(resolve => {
    let settled = false
    let settleTimer = null
    const settle = outcome => {
      if (settled) return
      settled = true
      if (settleTimer) clearTimeout(settleTimer)
      resolve(outcome)
    }

    let blobUrl = null
    try {
      blobUrl = URL.createObjectURL(blob)
      const iframe = document.createElement('iframe')
      iframe.style.position = 'fixed'
      iframe.style.right = '0'
      iframe.style.bottom = '0'
      iframe.style.width = '0'
      iframe.style.height = '0'
      iframe.style.border = '0'
      iframe.setAttribute('aria-hidden', 'true')
      iframe.src = blobUrl
      iframe.onload = () => {
        // Any settlement is final. A load that arrives after the safety timer belongs to
        // a pipeline that has already recorded the label as unprinted and told the operator
        // to use the link: printing now would put an unannounced copy on the printer, and a
        // tab or a notice would arrive for an outcome the caller can no longer act on.
        if (settled) return
        try {
          iframe.contentWindow.focus()
          iframe.contentWindow.print()
          settle('iframe')
        } catch (error) {
          notify(onFallback, error)
          settle(openInNewTab(url) ? 'fallback' : 'blocked')
        }
      }
      const helper = { iframe, blobUrl, cleanupTimer: null }
      disposeHelper(activeHelper)
      activeHelper = helper
      document.body.appendChild(iframe)
      settleTimer = setTimeout(() => settle('timeout'), PRINT_SETTLE_TIMEOUT_MS)
      // Generous cleanup so the print job spools before the blob is revoked.
      helper.cleanupTimer = setTimeout(() => disposeHelper(helper), CLEANUP_MS)
    } catch (error) {
      // No blob: URL support, or a document we cannot append to.
      if (blobUrl) URL.revokeObjectURL(blobUrl)
      notify(onFallback, error)
      settle(openInNewTab(url) ? 'fallback' : 'blocked')
    }
  })

export const printPdfSilently = async (url, { onFallback } = {}) => {
  if (!url) return 'skipped'

  let blob
  try {
    blob = await fetchPdfBlob(url)
  } catch (error) {
    notify(onFallback, error)
    return openInNewTab(url) ? 'fallback' : 'blocked'
  }

  return printBlobInIframe(blob, url, onFallback)
}
